import { call, put, takeEvery, takeLeading } from 'redux-saga/effects';
import { createActions } from 'redux-actions';
import normalize from 'jsonapi-normalizer';

import { apiClients, readJsonApiErrors } from '../../../api';
import { resourceReducer } from '../../utils/resourceReducer';

// A re-read replaces the cached list instead of merging into it, so it never
// keeps a client deleted elsewhere.
const [resourceActions, resourceReducerFn, resourceSagas] = resourceReducer(
  'api-clients',
  { mergeOnIndex: false }
);

// Rotating a secret is the one call the factory has no verb for.
export const actions = {
  ...resourceActions,
  ...createActions({
    API_CLIENTS: {
      ROTATE_REQUEST_BEGIN: (id) => id,
      ROTATE_REQUEST_SUCCESS: (data) => data,
      ROTATE_REQUEST_FAILURE: (data) => data,
    },
  }).apiClients,
};

// The factory's create, update and destroy handlers return a slice without
// `meta` (a KNOWN BUG pinned in its tests), and `meta.availableScopes` has to
// outlive a save: the form on the next page is built from it.
export const reducer = (state, action) => {
  const next = resourceReducerFn(state, action);
  return next.meta ? next : { ...state, ...next };
};

// `clientSecret` is in a response only once. It travels on the action as
// `secret`, for the page that shows it, and stays out of the entity cache,
// which outlives that page.
const normalizeClient = (result) => {
  const { clientSecret, ...attributes } = result.data.attributes;
  const normalized = normalize({
    ...result,
    data: { ...result.data, attributes },
  });

  return clientSecret
    ? { ...normalized, secret: { id: result.data.id, clientSecret } }
    : normalized;
};

// The JSON:API errors go on the failure action for the form to show. A
// rejected form (400) or a conflict such as the client limit (409) is the
// customer's to fix, not an app error.
function* rejectForm(failureAction, error) {
  const errors = yield call(readJsonApiErrors, error);
  yield put(failureAction({ error, errors }));

  if (error.status !== 400 && error.status !== 409) {
    yield put({ type: 'ERRORS/HANDLE_API_ERROR', payload: error });
  }
}

function* readApiClients() {
  yield takeEvery(actions.indexRequestBegin, function* () {
    try {
      const result = yield call(apiClients.get);
      // The normalizer drops the top-level meta, which carries
      // availableScopes.
      yield put(
        actions.indexRequestSuccess({
          ...normalize(result),
          meta: result.meta,
        })
      );
    } catch (error) {
      yield put(actions.indexRequestFailure({ error }));
      // A session from before it asked for `api-clients:manage` is refused
      // until the customer signs in again. Expected, and the page says so.
      if (error.status !== 403) {
        yield put({ type: 'ERRORS/HANDLE_API_ERROR', payload: error });
      }
    }
  });
}

function* createApiClient() {
  yield takeLeading(actions.createRequestBegin, function* ({ payload }) {
    try {
      const result = yield call(apiClients.create, payload.data);
      yield put(actions.createRequestSuccess(normalizeClient(result)));
    } catch (error) {
      yield call(rejectForm, actions.createRequestFailure, error);
    }
  });
}

function* updateApiClient() {
  yield takeLeading(actions.updateRequestBegin, function* ({ payload }) {
    try {
      const result = yield call(apiClients.update, payload.data);
      yield put(actions.updateRequestSuccess(normalizeClient(result)));
    } catch (error) {
      yield call(rejectForm, actions.updateRequestFailure, error);
    }
  });
}

function* destroyApiClient() {
  yield takeLeading(actions.destroyRequestBegin, function* ({ payload: id }) {
    try {
      yield call(apiClients.destroy, id);
      yield put(actions.destroyRequestSuccess(id));
    } catch (error) {
      yield put(actions.destroyRequestFailure({ error }));
      yield put({ type: 'ERRORS/HANDLE_API_ERROR', payload: error });
    }
  });
}

function* rotateApiClientSecret() {
  yield takeLeading(actions.rotateRequestBegin, function* ({ payload: id }) {
    try {
      const result = yield call(apiClients.rotateSecret, id);
      yield put(actions.rotateRequestSuccess(normalizeClient(result)));
    } catch (error) {
      yield put(actions.rotateRequestFailure({ error }));
      yield put({ type: 'ERRORS/HANDLE_API_ERROR', payload: error });
    }
  });
}

export const sagas = [
  readApiClients,
  createApiClient,
  updateApiClient,
  destroyApiClient,
  rotateApiClientSecret,
  ...resourceSagas,
];
